import { Component, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, Compass, RotateCcw, WifiSlash, ArrowRight } from './Icons';
import { Brand } from './Brand';

type PageStateProps = {
  kind?: 'error' | 'not-found' | 'loading';
  title?: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
};

export function PageState({
  kind = 'error',
  title,
  description,
  onRetry,
  retryLabel = '重新尝试',
}: PageStateProps) {
  const heading = useRef<HTMLHeadingElement>(null);
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (kind !== 'loading') heading.current?.focus({ preventScroll: true });
  }, [kind]);
  useEffect(() => {
    setSlow(false);
    if (kind !== 'loading') return undefined;
    const timer = window.setTimeout(() => setSlow(true), 2_000);
    return () => window.clearTimeout(timer);
  }, [kind]);
  const Icon = kind === 'loading' ? BookOpen : kind === 'not-found' ? Compass : WifiSlash;
  return (
    <section className={'page-state page-state--' + kind} aria-busy={kind === 'loading'}>
      {kind === 'loading' ? (
        <div className="page-state-skeleton" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      ) : (
        <span className="state-symbol">
          <Icon size={40} weight="duotone" aria-hidden="true" />
        </span>
      )}
      <span className="eyebrow">
        {kind === 'not-found'
          ? '404 / A DIFFERENT CHAPTER'
          : kind === 'loading'
            ? '加载中'
            : 'LET’S TRY AGAIN'}
      </span>
      <h1 tabIndex={-1} ref={heading}>
        {title ??
          (kind === 'not-found'
            ? '这一页，暂时找不到了。'
            : kind === 'loading'
              ? '加载中…'
              : '这一页，暂时没能打开。')}
      </h1>
      <p role={kind === 'loading' ? 'status' : undefined}>
        {description ??
          (kind === 'not-found'
            ? '链接可能已经变更，也可能是地址写错了。回到首页，或者从练习目录重新出发。'
            : kind === 'loading'
              ? '正在准备页面内容。'
              : '请稍后再试，你也可以先回到首页。')}
        {kind === 'loading' && slow && (
          <>
            <br />
            <span className="page-state-slow-note">网络较慢，仍在继续加载，请稍候。</span>
          </>
        )}
      </p>
      {kind !== 'loading' && (
        <div className="actions">
          {onRetry ? (
            <button className="primary" onClick={onRetry}>
              {retryLabel}
              <RotateCcw size={18} aria-hidden="true" />
            </button>
          ) : (
            <Link className="button primary" to="/practice">
              浏览练习 <ArrowRight size={18} />
            </Link>
          )}
          <Link className="button subtle" to="/">
            返回首页
          </Link>
        </div>
      )}
    </section>
  );
}

class Boundary extends Component<{ children: ReactNode; resetKey: string }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidUpdate(previous: Readonly<{ resetKey: string }>) {
    if (previous.resetKey !== this.props.resetKey && this.state.failed)
      this.setState({ failed: false });
  }
  render() {
    return this.state.failed ? (
      <PageState
        description="页面遇到了一点问题。可以重新打开，或先返回首页；本机备份的文字可在重新进入练习后检查。"
        onRetry={() => this.setState({ failed: false })}
      />
    ) : (
      this.props.children
    );
  }
}
export function PageBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <Boundary resetKey={pathname}>{children}</Boundary>;
}
export function StandalonePageState(props: PageStateProps) {
  return (
    <div className="site-failure">
      <a className="skip-link" href="#main-content">
        跳至主要内容
      </a>
      <header className="site-header">
        <div className="site-header-inner">
          <Brand />
          <nav className="state-header-nav" aria-label="网站导航">
            <Link to="/practice">浏览练习</Link>
          </nav>
        </div>
      </header>
      <main id="main-content" tabIndex={-1}>
        <PageState {...props} />
      </main>
    </div>
  );
}
export function SiteFailure() {
  return <StandalonePageState onRetry={() => window.location.reload()} />;
}
